/**
 * useMatchSuggestions — Real-time pending match suggestions for review queue
 *
 * Reads users/{uid}/match_suggestions where status === 'pending'.
 * These are server-generated (checkForMatchSuggestions in earned.ts)
 * when a Pool 2 verdict has a potential matching ART_INCOME transaction.
 */

import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { useAuth } from '@/contexts/AuthContext';

export interface MatchSuggestion {
  id: string;
  verdictId: string;
  transactionId: string;
  verdictTitle: string;
  verdictSoldPrice: number;
  txnAmount: number;
  txnMerchant: string;
  txnDate: string;
  matchPercent: number;
  daysApart: number;
  createdAt: string;
  status: 'pending' | 'accepted' | 'dismissed';
}

interface UseMatchSuggestionsReturn {
  suggestions: MatchSuggestion[];
  isLoading: boolean;
  acceptSuggestion: (suggestionId: string) => Promise<void>;
  dismissSuggestion: (suggestionId: string) => Promise<void>;
}

export function useMatchSuggestions(): UseMatchSuggestionsReturn {
  const { user } = useAuth();
  const [suggestions, setSuggestions] = useState<MatchSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setIsLoading(false);
      setSuggestions([]);
      return;
    }

    const sugRef = collection(db, 'users', user.uid, 'match_suggestions');
    const q = query(sugRef, where('status', '==', 'pending'));

    const unsub = onSnapshot(
      q,
      (snap) => {
        const items: MatchSuggestion[] = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        } as MatchSuggestion));
        setSuggestions(items);
        setIsLoading(false);
      },
      (err) => {
        console.error('[useMatchSuggestions] onSnapshot error:', err);
        setIsLoading(false);
      }
    );

    return () => unsub();
  }, [user]);

  const acceptSuggestion = async (suggestionId: string) => {
    if (!user) return;
    const ref = doc(db, 'users', user.uid, 'match_suggestions', suggestionId);
    await updateDoc(ref, { status: 'accepted' });
  };

  const dismissSuggestion = async (suggestionId: string) => {
    if (!user) return;
    const ref = doc(db, 'users', user.uid, 'match_suggestions', suggestionId);
    await updateDoc(ref, { status: 'dismissed' });
  };

  return { suggestions, isLoading, acceptSuggestion, dismissSuggestion };
}

export default useMatchSuggestions;
